import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import type { CareTaskDto, PlantDto } from '../types';
import { Card, Empty, ErrorBanner, Loader } from '../components/common';

interface Props {
  spaceId: string;
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

// Цвет точки — тип задачи; легенда под сеткой.
const TYPE_STYLE: Record<string, { color: string; label: string }> = {
  watering_check: { color: '#3b82f6', label: 'полив' },
  fertilizing: { color: '#d99a2b', label: 'подкормка' },
  treatment: { color: '#d8544f', label: 'обработка' },
  spraying: { color: '#d8544f', label: 'обработка' },
  pruning: { color: '#8b5cf6', label: 'обрезка' },
  repotting_check: { color: '#a16207', label: 'пересадка' },
  diagnosis_followup: { color: '#14b8a6', label: 'проверка' },
  protection: { color: '#0ea5e9', label: 'защита от погоды' },
};
const OTHER_STYLE = { color: '#8e8e93', label: 'другое' };
const MAX_DOTS = 4;

function dayKey(date: Date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Выполненная задача — в день выполнения, остальные — в день по плану. */
function taskDate(task: CareTaskDto) {
  return new Date(task.status === 'completed' && task.completedAt ? task.completedAt : task.scheduledAt);
}

function gridStart(month: Date) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const shift = (first.getDay() + 6) % 7; // понедельник — первый день недели
  return new Date(first.getFullYear(), first.getMonth(), 1 - shift);
}

export function Calendar({ spaceId }: Props) {
  const today = new Date();
  const [month, setMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [plantId, setPlantId] = useState('');
  const [plants, setPlants] = useState<PlantDto[]>([]);
  const [tasks, setTasks] = useState<CareTaskDto[]>([]);
  const [selected, setSelected] = useState<string>(dayKey(today));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const days = useMemo(() => {
    const start = gridStart(month);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [month]);

  useEffect(() => {
    api.plants(spaceId).then(setPlants).catch(() => setPlants([]));
  }, [spaceId]);

  useEffect(() => {
    setLoading(true);
    const from = days[0];
    const to = new Date(days[41].getFullYear(), days[41].getMonth(), days[41].getDate() + 1);
    api
      .calendar(spaceId, from, to, plantId || undefined)
      .then((items) => {
        setTasks(items);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Ошибка загрузки'))
      .finally(() => setLoading(false));
  }, [spaceId, plantId, days]);

  const byDay = useMemo(() => {
    const map = new Map<string, CareTaskDto[]>();
    for (const task of tasks) {
      const key = dayKey(taskDate(task));
      const bucket = map.get(key) ?? [];
      bucket.push(task);
      map.set(key, bucket);
    }
    return map;
  }, [tasks]);

  // Сводка по текущему месяцу: сколько раз каждое растение требовало внимания.
  const summary = useMemo(() => {
    const inMonth = tasks.filter((t) => taskDate(t).getMonth() === month.getMonth());
    const perPlant = new Map<string, number>();
    for (const task of inMonth) perPlant.set(task.plantName, (perPlant.get(task.plantName) ?? 0) + 1);
    return {
      total: inMonth.length,
      done: inMonth.filter((t) => t.status === 'completed').length,
      perPlant: Array.from(perPlant.entries()).sort((a, b) => b[1] - a[1]),
    };
  }, [tasks, month]);

  const title = month.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  const selectedTasks = byDay.get(selected) ?? [];
  const selectedDate = days.find((d) => dayKey(d) === selected);
  const usedTypes = Array.from(new Set(tasks.map((t) => TYPE_STYLE[t.type]?.label ?? OTHER_STYLE.label)));

  function shiftMonth(delta: number) {
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  }

  return (
    <div>
      <h1>Календарь</h1>

      <div className="row between month-nav">
        <button className="btn secondary small" onClick={() => shiftMonth(-1)} aria-label="Предыдущий месяц">
          ‹
        </button>
        <span className="title month-title">{title}</span>
        <button className="btn secondary small" onClick={() => shiftMonth(1)} aria-label="Следующий месяц">
          ›
        </button>
      </div>

      <div className="plant-chips mt" role="tablist" aria-label="Растение">
        <button className={`plant-chip ${plantId === '' ? 'active' : ''}`} onClick={() => setPlantId('')}>
          <span className="chip-avatar">🌿</span>
          Все растения
        </button>
        {plants.map((plant) => (
          <button
            key={plant.id}
            className={`plant-chip ${plantId === plant.id ? 'active' : ''}`}
            onClick={() => setPlantId(plant.id)}
          >
            {plant.photoUrl ? (
              <img className="chip-avatar" src={plant.photoUrl} alt="" />
            ) : (
              <span className="chip-avatar">🪴</span>
            )}
            {plant.displayName}
          </button>
        ))}
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      <div className="month-grid mt">
        {WEEKDAYS.map((name) => (
          <div key={name} className="weekday">
            {name}
          </div>
        ))}
        {days.map((date) => {
          const key = dayKey(date);
          const items = byDay.get(key) ?? [];
          const outside = date.getMonth() !== month.getMonth();
          const classes = [
            'day-cell',
            outside ? 'outside' : '',
            key === dayKey(today) ? 'is-today' : '',
            key === selected ? 'is-selected' : '',
          ].join(' ');
          return (
            <button key={key} className={classes} onClick={() => setSelected(key)}>
              <span className="day-number">{date.getDate()}</span>
              <span className="dots">
                {items.slice(0, MAX_DOTS).map((task) => {
                  const style = TYPE_STYLE[task.type] ?? OTHER_STYLE;
                  const done = task.status === 'completed';
                  return (
                    <span
                      key={task.id}
                      className="dot"
                      style={{
                        background: done ? style.color : 'transparent',
                        borderColor: style.color,
                      }}
                    />
                  );
                })}
                {items.length > MAX_DOTS ? <span className="more">+{items.length - MAX_DOTS}</span> : null}
              </span>
            </button>
          );
        })}
      </div>

      {usedTypes.length ? (
        <div className="legend hint mt">
          {Object.entries(TYPE_STYLE)
            .filter(([, s], i, all) => usedTypes.includes(s.label) && all.findIndex(([, x]) => x.label === s.label) === i)
            .map(([type, s]) => (
              <span key={type}>
                <span className="dot" style={{ background: s.color, borderColor: s.color }} /> {s.label}
              </span>
            ))}
          <span>
            <span className="dot" style={{ borderColor: OTHER_STYLE.color }} /> запланировано
          </span>
        </div>
      ) : null}

      {loading ? <Loader /> : null}

      <Card className="mt">
        <div className="title">
          {selectedDate
            ? selectedDate.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })
            : 'Выберите день'}
        </div>
        {!selectedTasks.length ? <div className="hint mt">В этот день задач нет</div> : null}
        {selectedTasks.map((task) => {
          const style = TYPE_STYLE[task.type] ?? OTHER_STYLE;
          const done = task.status === 'completed';
          return (
            <div key={task.id} className="calendar-day">
              <span
                className="dot"
                style={{ background: done ? style.color : 'transparent', borderColor: style.color }}
              />
              <span className="grow">
                {task.plantName} — {task.details?.product ? `обработка «${task.details.product}»` : task.title}
              </span>
              <span className="hint">{done ? '✅ сделано' : task.status === 'snoozed' ? 'перенесено' : 'по плану'}</span>
            </div>
          );
        })}
      </Card>

      {summary.total ? (
        <Card>
          <div className="title">За {month.toLocaleDateString('ru-RU', { month: 'long' })}</div>
          <div className="hint">
            {summary.total} задач, выполнено {summary.done}
          </div>
          {!plantId
            ? summary.perPlant.map(([name, count]) => (
                <div key={name} className="row between mt">
                  <span>{name}</span>
                  <span className="badge">{count}</span>
                </div>
              ))
            : null}
        </Card>
      ) : !loading ? (
        <Empty title="В этом месяце задач нет" />
      ) : null}
    </div>
  );
}
